window.mobileGalleryLocale = window.mobileGalleryLocale || {
  noImageText: "{{ 'products.product.media.no_image_available' | t | default: 'Sorry, this image is not available.' }}"
};
if (!customElements.get("media-gallery")) {
  class MediaGallery extends HTMLElement {
    static INIT_CLASS = 'js-initialized';

    constructor() {
      super();
      if (window.innerWidth < 769) {
          return;
      }
      // Initialize state
      this.state = {
        currentIndex: 0,
        currentItem: null,
        currentThumb: null,
        currentGroup: null,
        viewerItemOffset: 0,
        isInitialized: false
      };

      // Bind methods once
      this.handleScroll = this.debounce(this.handleScroll.bind(this), 16);
      this.handleResize = this.debounce(this.handleResize.bind(this), 100);
      this.handlePaginationClick = this.handlePaginationClick.bind(this);
      this.handleNavClick = this.handleNavClick.bind(this);
      this.handleThumbClick = this.handleThumbClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.zoomEventListener = this.handleZoomMouseMove.bind(this);

      // Initialize with delay in design mode
      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      // Remove all event listeners
      const events = [
        { target: window, event: "on:debounced-resize", handler: this.handleResize },
        { target: window, event: "on:debounced-resize", handler: this.resizeInitHandler },
        { target: window, event: "on:debounced-resize", handler: this.zoomInitHandler }
      ];

      events.forEach(({ target, event, handler }) => {
        if (handler) target.removeEventListener(event, handler);
      });

      // Clean up pagination dots
      this.paginationDots?.forEach(dot => {
        dot.removeEventListener("click", this.handlePaginationClick);
      });

      // Clean up zoom links
      this.zoomLinks?.forEach(link => {
        link.removeEventListener("mousemove", this.zoomEventListener);
        link.removeEventListener("click", this.zoomEventListener);
      });

      this.hideNoImageOverlay();

      // Removed initialization class
      this.classList.remove(MediaGallery.INIT_CLASS);
    }

    init() {
      if (this.state.isInitialized) return;

      this.cacheElements();
      this.setupConfiguration();
      this.initializeFeatures();
      this.attachEventListeners();

      this.state.isInitialized = true;

      // Added a class to show media gallery is initialized
      this.classList.add(MediaGallery.INIT_CLASS);
    }

    cacheElements() {
      // Cache all DOM elements at once
      const selectors = {
        section: ".js-product",
        viewer: ".media-viewer",
        thumbs: ".media-thumbs",
        thumbsItems: ".media-thumbs__item",
        controls: ".media-ctrl",
        prevBtn: '.media-ctrl__btn[name="prev"]',
        nextBtn: '.media-ctrl__btn[name="next"]',
        counterCurrent: ".media-ctrl__current-item",
        counterTotal: ".media-ctrl__total-items",
        liveRegion: ".media-gallery__status",
        zoomLinks: ".js-zoom-link",
        loadingSpinner: ".loading-spinner",
        xrButton: ".media-xr-button",
        paginationDots: ".pagination-dot"
      };

      Object.entries(selectors).forEach(([key, selector]) => {
        if (selector.includes("All") || key === "thumbsItems" || key === "zoomLinks" || key === "paginationDots") {
          this[key] = this.querySelectorAll(selector);
        } else {
          this[key] = key === "section" ? this.closest(selector) : this.querySelector(selector);
        }
      });
    }

    setupConfiguration() {

      const hasZoomTrigger = !!this.dataset.zoomTrigger;
      const hasZoomEnabled = this.hasAttribute("data-zoom-enabled");
      const shouldEnableZoom = hasZoomEnabled || hasZoomTrigger;

      this.config = {
        noSelectedVariant: this.hasAttribute("data-no-selected-variant"),
        mediaGroupingEnabled: this.hasAttribute("data-media-grouping-enabled") && this.getMediaGroupData(),
        stackedScroll: this.dataset.stackedScroll,
        stackedUnderline: this.dataset.stackedUnderline === "true" && !this.mediaGroupingEnabled,
        isFeatured: this.dataset.isFeatured === "true",
        layout: this.dataset.layout,
        zoomEnabled: shouldEnableZoom,
        zoomTrigger: this.dataset.zoomTrigger
      };
    }
     initializeFeatures() {
      if (this.config.zoomEnabled) {
        this.initializeZoomModal();
      }

      if (this.config.mediaGroupingEnabled && !this.config.noSelectedVariant) {
        this.initializeMediaGrouping();
      }

      if (this.config.layout === "stacked" && theme?.mediaMatches?.md) {
        this.initializeStackedLayout();
      } else {
        this.initGallery();
      }

      if (this.config.zoomEnabled) {
        this.initializeZoomFeature();
      }
    }

    initializeZoomModal() {
      const template = this.querySelector(".js-media-zoom-template");
      if (template) {
        this.galleryModal = template.content.firstElementChild.cloneNode(true);
      }
    }

    initializeMediaGrouping() {
      this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());


      setTimeout(() => {
        if (this.zoomLinks?.length) {
          this.setupGridZoomLinks();
        }
      }, 100);
    }

    setupGridZoomLinks() {
      const mainGridSlots = Array.from(this.viewerItems || []).slice(0, 4);
      const gridZoomLinks = mainGridSlots
        .map(slot => slot.querySelector(".js-zoom-link"))
        .filter(Boolean);

      gridZoomLinks.forEach(zoomLink => {
        if (this.config.zoomTrigger === "hover") {
          zoomLink.addEventListener("mousemove", this.zoomEventListener);
        } else {
          zoomLink.addEventListener("click", this.preventDefaultAndTriggerZoom.bind(this));
        }
      });
    }

    preventDefaultAndTriggerZoom(evt) {
      this.triggerZoomInit();
      evt.preventDefault();
    }

    initializeStackedLayout() {
      this.resizeInitHandler = this.initGallery.bind(this);
      window.addEventListener("on:debounced-resize", this.resizeInitHandler);

      this.setVisibleItems();
      this.previousMediaItem = this.querySelector(".media-viewer__item.is-current-variant");

      setTimeout(() => {
        this.customSetActiveMedia(this.previousMediaItem, this.config.stackedScroll === "always");
      }, 200);
    }

    initializeZoomFeature() {
      if (this.config.zoomTrigger === "hover") {
        this.triggerZoomInit();
      } else {
        this.zoomLinks?.forEach(zoomLink => {
          zoomLink.addEventListener("click", this.preventDefaultAndTriggerZoom.bind(this));
        });
      }
    }

    attachEventListeners() {
      // Pagination dots
      if (this.paginationDots?.length) {
        this.paginationDots.forEach(dot => {
          dot.addEventListener("click", this.handlePaginationClick);
        });
      }

      // Variant change
      this.section?.addEventListener("on:variant:change", this.onVariantChange);
    }

    // Utility: Debounce function
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    // Utility: Throttle function
    throttle(func, limit) {
      let inThrottle;
      return function(...args) {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      window.addEventListener("on:debounced-resize", this.zoomInitHandler);
      this.initZoom();

      this.zoomLinks?.forEach(zoomLink => {
        zoomLink.addEventListener("click", evt => evt.preventDefault());
      });
    }

    handlePaginationClick(evt) {
      const dot = evt.target.closest(".pagination-dot");
      if (!dot) return;

      const mediaId = dot.dataset.mediaId;
      const itemToShow = this.findVisibleItem(mediaId) || this.findAnyItem(mediaId);

      if (itemToShow) {
        this.navigateToItem(itemToShow, dot);
      }
    }

    findVisibleItem(mediaId) {
      return Array.from(this.visibleItems || []).find(
        item => item.dataset.mediaId === mediaId
      );
    }

    findAnyItem(mediaId) {
      const allItems = this.querySelectorAll(`[data-media-id="${mediaId}"]`);
      return Array.from(allItems).find(
        item => item.offsetWidth > 0 && item.offsetHeight > 0
      );
    }

    navigateToItem(item, dot) {
      this.viewer?.scrollTo({
        left: item.offsetLeft,
        behavior: "smooth",
      });

      this.customSetActiveMedia(item, false);

      if (dot) {
        const dotIndex = parseInt(dot.dataset.index, 10);
        this.updatePaginationDots(dotIndex);
      }
    }

    onVariantChange(evt) {
      if (this.config.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      const variant = evt.detail?.variant;
      if (variant) {
        this.handleVariantMediaChange(variant);
      }
    }

    handleVariantMediaChange(variant) {
      if (variant.featured_media) {
        this.hideNoImageOverlay();

        const variantMedia = this.viewer?.querySelector(
          `[data-media-id="${variant.featured_media.id}"]`
        );

        if (variantMedia) {
          this.navigateToItem(variantMedia);
          this.updatePaginationForVariant(variant.featured_media.id);
        }
      } else {
        this.showNoImageOverlay();
      }
    }

    showNoImageOverlay() {
      this.hideNoImageOverlay();

      const overlay = document.createElement('div');
      overlay.className = 'media-gallery__no-image-overlay';
      overlay.innerHTML = `
        <div class="media-gallery__no-image-content">
          <div class="media-gallery__no-image-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2"/>
            </svg>
          </div>
          <p class="media-gallery__no-image-text">${window.mobileGalleryLocale?.noImageText}</p>
        </div>
      `;

      if (this.viewer) {
        this.viewer.style.position = 'relative';
        this.viewer.appendChild(overlay);
      }
    }

    hideNoImageOverlay() {
      const existingOverlay = this.viewer?.querySelector('.media-gallery__no-image-overlay');
      if (existingOverlay) {
        existingOverlay.remove();
      }
    }

    updatePaginationForVariant(mediaId) {
      const matchingDot = Array.from(this.paginationDots || []).find(
        dot => dot.dataset.mediaId === mediaId.toString()
      );

      if (matchingDot) {
        const dotIndex = parseInt(matchingDot.dataset.index, 10);
        this.updatePaginationDots(dotIndex);
      }
    }

    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section?.querySelectorAll('.option-selector');
      if (!optionSelectors?.length) return null;

      const selectedOptions = [];
      optionSelectors.forEach(selector => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedOption = selector.querySelector('.custom-select__btn');
          selectedOptions.push(selectedOption ? selectedOption.textContent.trim() : null);
        } else {
          const checkedInput = selector.querySelector('input:checked');
          selectedOptions.push(checkedInput ? checkedInput.value : null);
        }
      });
      const variantOption = selectedOptions.filter(Boolean).join(' / ');
      return variantOption;
    }

    getMediaGroupData() {
      if (this.variantMediaData === undefined) {
        const dataEl = this.querySelector(".js-data-variant-media");
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }
      return this.variantMediaData;
    }

    getMediaGroupMap() {
      if (this.mediaGroupMap) return this.mediaGroupMap;

      this.mediaGroupMap = { groups: {} };
      const variantMediaData = this.getMediaGroupData();
      if (!variantMediaData) return this.createFallbackGroupMap();

      this.viewerItems = this.querySelectorAll(".media-viewer__item");
      this.buildMediaGroupMap(variantMediaData);
      this.addGroupHelper();

      return this.mediaGroupMap;
    }

    buildMediaGroupMap(variantMediaData) {
      let currentMediaGroupKey = false;

      this.viewerItems.forEach(item => {
        const mediaId = parseInt(item.dataset.mediaId, 10);
        const variantMedia = variantMediaData.variantMedia.find(
          vm => vm.mediaId === mediaId
        );

        if (variantMedia) {
          currentMediaGroupKey = variantMedia.title || variantMedia.option;
        }

        if (currentMediaGroupKey) {
          this.addItemToGroup(currentMediaGroupKey, item);
        }
      });
    }

    addItemToGroup(groupName, item) {
      if (!this.mediaGroupMap.groups[groupName]) {
        this.mediaGroupMap.groups[groupName] = {
          name: groupName,
          items: []
        };
      }

      const groupItem = { main: item };

      if (this.thumbs) {
        groupItem.thumb = this.thumbs.querySelector(
          `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
        );
      }

      this.mediaGroupMap.groups[groupName].items.push(groupItem);
    }

    addGroupHelper() {
      this.mediaGroupMap.groupFromItem = (item) => {
        for (const [groupName, group] of Object.entries(this.mediaGroupMap.groups)) {
          if (group.items.some(groupItem => groupItem.main === item)) {
            return group;
          }
        }
        return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
      };
    }

    createFallbackGroupMap() {
      this.viewerItems = this.querySelectorAll(".media-viewer__item");
      this.mediaGroupMap.groups["all"] = { name: "all", items: [] };

      Array.from(this.viewerItems).forEach(mediaElement => {
        const groupItem = { main: mediaElement };

        if (this.thumbs) {
          groupItem.thumb = this.thumbs.querySelector(
            `[data-media-id="${mediaElement.dataset.mediaId}"].media-thumbs__item`
          );
        }

        this.mediaGroupMap.groups["all"].items.push(groupItem);
      });

      this.mediaGroupMap.groupFromItem = () => this.mediaGroupMap.groups["all"];
      return this.mediaGroupMap;
    }

    setActiveMediaGroup(groupName) {

      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.state.currentItem);

      if (!selectedGroup) {
        this.showNoImageOverlay();
        return;
      }

      if (this.state.currentGroup === selectedGroup) return;

      this.state.currentGroup = selectedGroup;
      this.hideLoadingSpinner();
      this.processGroupLayout(selectedGroup, groupName, mediaGroupMap);
      this.finalizeGroupSetup(selectedGroup);

       const reinitializeZoom = () => {
            // Get ALL zoom links
            this.zoomLinks = this.querySelectorAll(".js-zoom-link");

            // Filter to visible zoom links
            const visibleZoomLinks = Array.from(this.zoomLinks).filter(link => {
              const parentItem = link.closest(".media-viewer__item");
              return parentItem && parentItem.style.display !== "none" &&
                     parentItem.offsetWidth > 0 && parentItem.offsetHeight > 0;
            });

            // Reattach mousemove listeners
            visibleZoomLinks.forEach((zoomLink) => {
              // Remove any existing listener first
              zoomLink.removeEventListener("mousemove", this.zoomEventListener);

              // Add fresh mousemove listener
              zoomLink.addEventListener("mousemove", this.zoomEventListener);
              zoomLink.classList.remove("pointer-events-none");
            });

            // Reinitialize zoom styles
            if (visibleZoomLinks.length > 0) {
              visibleZoomLinks.forEach((el) => {
                const zoomImage = el.querySelector(".zoom-image");
                const productImage = el.querySelector(".product-image");

                if (zoomImage && productImage) {
                  const zoomWidth = Number(zoomImage.dataset.originalWidth || 0);
                  const imageWidth = productImage.getBoundingClientRect().width;

                  if (theme?.mediaMatches?.md && zoomWidth - 75 > imageWidth) {
                    const scaleFactor = 1.5;
                    zoomImage.style.transform = `scale(${scaleFactor})`;
                    zoomImage.style.transformOrigin = "center center";
                    el.classList.remove("pointer-events-none");
                  }
                }
              });
            }
          };

          // Multiple attempts to catch all images including async slider content
          setTimeout(reinitializeZoom, 100);
          setTimeout(reinitializeZoom, 300);
          setTimeout(reinitializeZoom, 600);
    }

    hideLoadingSpinner() {
      this.loadingSpinner?.classList.add("loading-spinner--out");
    }

    processGroupLayout(selectedGroup, groupName, mediaGroupMap) {
      // Store original content on first switch
      if (!this.originalGridContent) {
        this.storeOriginalGridContent();
      }

      const mainGridSlots = Array.from(this.viewerItems || []).slice(0, 4);
      const firstVariantName = Object.keys(mediaGroupMap.groups)[0];
      const isFirstVariant = groupName === firstVariantName;

      this.processGridSlots(mainGridSlots, selectedGroup, isFirstVariant);
      this.preserveLifestyleContent();
      this.updateSwiper(selectedGroup, this.countVariantMediaInGrid(mainGridSlots));
    }

    storeOriginalGridContent() {
      const mainGridSlots = Array.from(this.viewerItems || []).slice(0, 4);
      this.originalGridContent = mainGridSlots.map(slot => ({
        innerHTML: slot.innerHTML,
        mediaId: slot.dataset.mediaId,
        mediaType: slot.dataset.mediaType,
        className: slot.className,
        hasLifestyleContent: slot.hasAttribute('data-lifestyle-content'),
        isLifestylePosition: ['2', '3'].includes(slot.dataset.sequencePosition)
      }));
    }

    hideAllItems() {
      this.viewerItems?.forEach(item => {
        if (!item.hasAttribute('data-lifestyle-content')) {
          item.style.display = "none";
        }
        item.classList.remove("media-viewer__item--single");
      });

      this.thumbsItems?.forEach(item => {
        item.style.display = "none";
      });
    }

    processGridSlots(mainGridSlots, selectedGroup, isFirstVariant) {
      let variantMediaIndex = 0;

      mainGridSlots.forEach((gridSlot, gridIndex) => {
        const isLifestylePosition = ['2', '3'].includes(gridSlot.dataset.sequencePosition);
        const hasLifestyleContent = gridSlot.hasAttribute('data-lifestyle-content');

        if (isFirstVariant && this.originalGridContent?.[gridIndex]) {
          this.restoreOriginalContent(gridSlot, gridIndex);
          if (!this.originalGridContent[gridIndex].hasLifestyleContent) {
            variantMediaIndex++;
          }
          return;
        }

        if (isLifestylePosition && hasLifestyleContent) {
          gridSlot.style.display = "";
          return;
        }

        if (variantMediaIndex < selectedGroup.items.length) {
          this.setGridSlotContent(gridSlot, selectedGroup.items[variantMediaIndex]);
          variantMediaIndex++;
        } else {
          gridSlot.style.display = "none";
        }
      });

      return variantMediaIndex;
    }

    countVariantMediaInGrid(mainGridSlots) {
      return mainGridSlots.filter(slot =>
        !slot.hasAttribute('data-lifestyle-content') &&
        slot.style.display !== "none"
      ).length;
    }

    restoreOriginalContent(gridSlot, gridIndex) {
      const original = this.originalGridContent[gridIndex];

      gridSlot.innerHTML = original.innerHTML;
      gridSlot.dataset.mediaId = original.mediaId;
      gridSlot.dataset.mediaType = original.mediaType;
      gridSlot.className = original.className;

      if (original.hasLifestyleContent) {
        gridSlot.setAttribute('data-lifestyle-content', 'true');
      }

      gridSlot.style.display = "";
    }

    setGridSlotContent(gridSlot, groupItem) {
      const sourceItem = groupItem.main;

      gridSlot.innerHTML = sourceItem.innerHTML;
      gridSlot.dataset.mediaId = sourceItem.dataset.mediaId;
      gridSlot.dataset.mediaType = sourceItem.dataset.mediaType;
      gridSlot.className = sourceItem.className;
      gridSlot.removeAttribute('data-lifestyle-content');
      gridSlot.style.display = "";
    }

    showRemainingItems(selectedGroup, variantMediaIndex) {
      selectedGroup.items.forEach((item, index) => {
        if (index >= variantMediaIndex) {
          item.main.style.display = "";
        }

        if (item.thumb) {
          item.thumb.style.display = "";
        }
      });
    }

    preserveLifestyleContent() {
      const lifestyleItems = this.querySelectorAll('[data-lifestyle-content="true"]');
      lifestyleItems.forEach(item => {
        Object.assign(item.style, {
          display: "",
          opacity: "1",
          visibility: "visible"
        });
      });
    }

    updateSwiper(selectedGroup, variantMediaIndex) {
      const swiperContainer = this.querySelector("swiper-container");
      if (!swiperContainer) return;
      const allSlides = swiperContainer.querySelectorAll("swiper-slide");
      allSlides.forEach(slide => slide.style.display = "none");

      selectedGroup.items.forEach((item, index) => {
        if (index >= variantMediaIndex) {
          const targetSlide = Array.from(allSlides).find(slide => {
            const slideMediaId = slide.querySelector("[data-media-id]")?.dataset.mediaId;
            return slideMediaId === item.main.dataset.mediaId;
          });

          if (targetSlide) {
            targetSlide.style.display = "";
          }
        }
      });

      // Simple check and defer if swiper isn't ready
      if (swiperContainer.swiper?.update) {
        swiperContainer.swiper.update();
        swiperContainer.swiper.slideTo(0, 0);
      } else {
        // Defer the update to next tick when swiper might be ready
        setTimeout(() => {
          if (swiperContainer.swiper?.update) {
            swiperContainer.swiper.update();
            swiperContainer.swiper.slideTo(0, 0);
          }
        }, 0);
      }
    }

    finalizeGroupSetup(selectedGroup) {
      this.setVisibleItems();

      // Set active media
      const currentItemIsVisible = selectedGroup.items.some(
        item => item.main === this.state.currentItem
      );

      if (!currentItemIsVisible && selectedGroup.items.length > 0) {
        this.customSetActiveMedia(selectedGroup.items[0].main, true);
      }

      // Handle single item
      if (selectedGroup.items.length === 1) {
        selectedGroup.items[0].main.classList.add("media-viewer__item--single");
      }

      // Update pagination
      if (this.paginationDots?.length) {
        this.updatePaginationDotsForGroup(selectedGroup.items);
      }

      // Reinitialize zoom
      this.reinitializeZoom();
    }

    reinitializeZoom() {
      if (!this.config.zoomEnabled) return;

      setTimeout(() => {
        this.setVisibleItems();
        this.zoomLinks = this.querySelectorAll(".js-zoom-link");

        this.zoomLinks.forEach(zoomLink => {
          // Remove existing listeners
          zoomLink.removeEventListener("mousemove", this.zoomEventListener);
          zoomLink.removeEventListener("click", this.zoomEventListener);

          // Re-add based on trigger type
          if (this.config.zoomTrigger === "hover") {
            zoomLink.addEventListener("mousemove", this.zoomEventListener);
            zoomLink.classList.remove("pointer-events-none");
          } else {
            zoomLink.addEventListener("click", this.preventDefaultAndTriggerZoom.bind(this));
          }
        });

        this.initZoom();
      }, 100);
    }

showAllItems() {
  this.viewerItems?.forEach(item => item.style.display = "");
  this.thumbsItems?.forEach(item => item.style.display = "");

  const swiperContainer = this.querySelector("swiper-container");
  if (swiperContainer?.swiper) {
    const allSlides = swiperContainer.querySelectorAll("swiper-slide");
    allSlides.forEach(slide => slide.style.display = "");
    swiperContainer.swiper.update();
  }

  this.hideLoadingSpinner();

  // Reinitialize zoom for swiper content
  const reinitializeZoom = () => {
    this.zoomLinks = this.querySelectorAll(".js-zoom-link");

    const visibleZoomLinks = Array.from(this.zoomLinks).filter(link => {
      const parentItem = link.closest(".media-viewer__item");
      return parentItem && parentItem.style.display !== "none" &&
             parentItem.offsetWidth > 0 && parentItem.offsetHeight > 0;
    });

    visibleZoomLinks.forEach((zoomLink) => {
      zoomLink.removeEventListener("mousemove", this.zoomEventListener);
      zoomLink.addEventListener("mousemove", this.zoomEventListener);
      zoomLink.classList.remove("pointer-events-none");
    });

    visibleZoomLinks.forEach((el) => {
      const zoomImage = el.querySelector(".zoom-image");
      const productImage = el.querySelector(".product-image");

      if (zoomImage && productImage) {
        const zoomWidth = Number(zoomImage.dataset.originalWidth || 0);
        const imageWidth = productImage.getBoundingClientRect().width;

        if (theme?.mediaMatches?.md && zoomWidth - 75 > imageWidth) {
          const scaleFactor = 1.5;
          zoomImage.style.transform = `scale(${scaleFactor})`;
          zoomImage.style.transformOrigin = "center center";
          el.classList.remove("pointer-events-none");
        }
      }
    });
  };

  setTimeout(reinitializeZoom, 100);
}


    updatePaginationDotsForGroup(groupItems) {
      if (!this.paginationDots?.length) return;

      // Hide all dots
      this.paginationDots.forEach(dot => {
        dot.style.display = "none";
        dot.classList.remove("is-active");
        dot.setAttribute("aria-current", "false");
      });

      // Show relevant dots
      let visibleIndex = 0;
      groupItems.forEach(item => {
        const mediaId = item.main.dataset.mediaId;
        if (!mediaId) return;

        const matchingDot = Array.from(this.paginationDots).find(
          dot => dot.dataset.mediaId === mediaId
        );

        if (matchingDot) {
          matchingDot.style.display = "";
          matchingDot.dataset.index = visibleIndex.toString();

          if (visibleIndex === 0) {
            matchingDot.classList.add("is-active");
            matchingDot.setAttribute("aria-current", "true");
          }

          visibleIndex++;
        }
      });

      // Reattach click handlers
      this.reattachPaginationHandlers();
    }

    reattachPaginationHandlers() {
      setTimeout(() => {
        this.paginationDots.forEach(dot => {
          if (dot.style.display !== "none") {
            const newDot = dot.cloneNode(true);
            dot.parentNode.replaceChild(newDot, dot);
            newDot.addEventListener("click", this.handlePaginationClick);
          }
        });

        this.paginationDots = this.querySelectorAll(".pagination-dot");
      }, 100);
    }

    initGallery() {
      this.setVisibleItems();
      if ((this.visibleItems?.length || 0) <= 1) return;

      this.calculateOffsets();
      this.setCurrentItems();
      this.addGalleryListeners();
      this.setupThumbnails();
      this.setupInitialPagination();
      this.eagerLoadImages();
      this.setInitialActiveMedia();
    }

    calculateOffsets() {
      this.state.viewerItemOffset =
        this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.state.currentIndex = Math.round(
        this.viewer.scrollLeft / this.state.viewerItemOffset
      );
    }

    setCurrentItems() {
      this.state.currentItem = this.visibleItems[this.state.currentIndex];
    }

    addGalleryListeners() {
      this.viewer?.addEventListener("scroll", this.handleScroll);
      this.controls?.addEventListener("click", this.handleNavClick);
      this.thumbs?.addEventListener("click", this.handleThumbClick);
      window.addEventListener("on:debounced-resize", this.handleResize);
    }

    setupThumbnails() {
      if (this.thumbs && this.state.currentItem) {
        this.state.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.state.currentItem.dataset.mediaId}"]`
        );
      }
    }

    setupInitialPagination() {
      if (!this.paginationDots?.length) return;

      const currentItem = this.querySelector(".media-viewer__item.is-current-variant");
      if (currentItem) {
        const mediaId = currentItem.dataset.mediaId;
        const matchingDot = Array.from(this.paginationDots).find(
          dot => dot.dataset.mediaId === mediaId
        );

        if (matchingDot) {
          const dotIndex = parseInt(matchingDot.dataset.index, 10);
          this.updatePaginationDots(dotIndex);
        }
      }
    }

    eagerLoadImages() {
      if (!this.config.isFeatured && document.hasFocus()) {
        this.viewer?.querySelectorAll('.product-image[loading="lazy"]')
          .forEach(img => img.loading = "eager");
      }
    }

    setInitialActiveMedia() {
      const currentItem = this.querySelector(".media-viewer__item.is-current-variant");
      this.customSetActiveMedia(currentItem, true);
    }

    initZoom() {
      this.zoomLinks?.forEach((el, index) => {
        const zoomImage = el.querySelector(".zoom-image");
        const productImage = el.querySelector(".product-image");

        if (!zoomImage || !productImage) return;

        const zoomWidth = Number(zoomImage.dataset.originalWidth || 0);
        const imageWidth = productImage.getBoundingClientRect().width;

        if (theme?.mediaMatches?.md && zoomWidth - 75 > imageWidth) {
          this.enableZoom(el, zoomImage);
        } else {
          this.disableZoom(el);
        }
      });
    }

    enableZoom(el, zoomImage) {
      const scaleFactor = 1.5;

      Object.assign(zoomImage.style, {
        transform: `scale(${scaleFactor})`,
        transformOrigin: "center center"
      });

      el.addEventListener("mousemove", this.zoomEventListener);
      el.classList.remove("pointer-events-none");
    }

    disableZoom(el) {
      el.removeEventListener("mousemove", this.zoomEventListener);
      el.classList.add("pointer-events-none");
    }

    handleZoomMouseMove(evt) {
      if (this.config.zoomEnabled && !this.config.zoomTrigger &&
          !evt.target.closest(".gallery-zoom-modal")) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector(".js-zoom-image");

      if (zoomImage?.dataset.src) {
        this.loadZoomImage(zoomImage, hoverElem);
      }

      this.updateZoomPosition(evt, zoomImage);
    }

    loadZoomImage(zoomImage, hoverElem) {
      this.loadingSpinner?.classList.remove("loading-spinner--out");

      const img = new Image();
      img.src = zoomImage.dataset.src;
      img.onload = () => {
        zoomImage.src = img.src;
        hoverElem.classList.remove("media--zoom-not-loaded");
        this.loadingSpinner?.classList.add("loading-spinner--out");
      };
      zoomImage.removeAttribute("data-src");
    }

    updateZoomPosition(evt, zoomImage) {
      try {
        const offsetX = evt.offsetX ?? evt.touches?.[0]?.pageX;
        const offsetY = evt.offsetY ?? evt.touches?.[0]?.pageY;

        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;

        Object.assign(zoomImage.style, {
          objectPosition: `${x}% ${y}%`,
          transform: "scale(1.5)",
          transformOrigin: `${x}% ${y}%`
        });
      } catch (err) {
        console.warn("Zoom position update failed:", err);
      }
    }

    updatePaginationDots(activeIndex) {
      if (!this.paginationDots?.length) return;

      this.paginationDots.forEach(dot => {
        if (dot.style.display === "none") return;

        const dotIndex = parseInt(dot.dataset.index, 10);
        const isActive = dotIndex === activeIndex;

        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
      });
    }

    handleScroll() {
      const newIndex = Math.round(
        this.viewer.scrollLeft / this.state.viewerItemOffset
      );

      if (newIndex === this.state.currentIndex) return;

      const viewerItemOffset = this.visibleItems?.length > 1
        ? this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft
        : 0;

      if (viewerItemOffset !== this.state.viewerItemOffset) return;

      this.updateCurrentIndex(newIndex);
      this.updatePaginationFromScroll();
      this.updateNavigationButtons();
    }

    updateCurrentIndex(newIndex) {
      this.state.currentIndex = newIndex;
      this.state.currentItem = this.visibleItems[newIndex];
    }

    updatePaginationFromScroll() {
      if (!this.paginationDots?.length || !this.state.currentItem) return;

      const mediaId = this.state.currentItem.dataset.mediaId;
      const matchingDot = Array.from(this.paginationDots).find(
        dot => dot.dataset.mediaId === mediaId && dot.style.display !== "none"
      );

      if (matchingDot) {
        const dotIndex = parseInt(matchingDot.dataset.index, 10);
        this.updatePaginationDots(dotIndex);
      }
    }

    updateNavigationButtons() {
      if (!this.controls) return;

      if (this.prevBtn) {
        this.prevBtn.disabled = this.state.currentIndex === 0;
      }

      if (this.nextBtn) {
        this.nextBtn.disabled = this.state.currentIndex === (this.visibleItems?.length || 1) - 1;
      }
    }

    handleNavClick(evt) {
      if (!evt.target.matches(".media-ctrl__btn")) return;

      const direction = evt.target === this.nextBtn ? 1 : -1;
      const targetIndex = this.state.currentIndex + direction;
      const itemToShow = this.visibleItems?.[targetIndex];

      if (itemToShow) {
        this.viewer?.scrollTo({
          left: itemToShow.offsetLeft,
          behavior: "smooth"
        });
      }
    }

    handleThumbClick(evt) {
      evt.preventDefault();
      const thumb = evt.target.closest("[data-media-id]");
      if (!thumb) return;

      const itemToShow = this.querySelector(
        `[data-media-id="${thumb.dataset.mediaId}"]`
      );

      if (itemToShow) {
        this.customSetActiveMedia(itemToShow);
        MediaGallery.playActiveMedia(itemToShow);
      }
    }

    handleResize() {
      this.updateViewerItemOffset();
      this.updateThumbVisibility();
    }

    updateViewerItemOffset() {
      if (this.visibleItems?.length > 1) {
        this.state.viewerItemOffset =
          this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      }
    }

    updateThumbVisibility() {
      if (this.thumbs && this.state.currentThumb) {
        this.checkThumbVisibilty(this.state.currentThumb);
      }
    }

    // Stub for variant-picker calls
    setActiveMedia() {}

    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (!mediaItem || mediaItem === this.state.currentItem) return;

      window.pauseAllMedia?.(this);
      this.state.currentItem = mediaItem;

      this.updateCurrentIndexFromMedia();
      this.updatePaginationFromMedia();
      this.handleStackedLayout(mediaItem);
      this.handleStandardLayout(scrollToItem);
      this.updateControls();
      this.announceChange();
      this.updateXRButton();
    }

    updateCurrentIndexFromMedia() {
      this.setVisibleItems();
      this.state.currentIndex = Array.from(this.visibleItems || [])
        .indexOf(this.state.currentItem);
    }

    updatePaginationFromMedia() {
      if (!this.paginationDots?.length || !this.state.currentItem) return;

      const mediaId = this.state.currentItem.dataset.mediaId;
      const matchingDot = Array.from(this.paginationDots).find(
        dot => dot.dataset.mediaId === mediaId
      );

      if (matchingDot) {
        const dotIndex = parseInt(matchingDot.dataset.index, 10);
        this.updatePaginationDots(dotIndex);
      }
    }

    handleStackedLayout(mediaItem) {
      if (this.config.layout !== "stacked" || !theme?.mediaMatches?.md) return;

      if (this.config.stackedUnderline) {
        this.previousMediaItem?.classList.remove("is-active");
        mediaItem.classList.add("is-active");
        this.previousMediaItem = mediaItem;
      }

      if (this.config.stackedScroll !== "never") {
        this.scrollToStackedItem(mediaItem);
      }
    }

    scrollToStackedItem(mediaItem) {
      const rect = mediaItem.getBoundingClientRect();
      const y = rect.top + window.pageYOffset - 150;
      const currentY = window.pageYOffset;

      // Only scroll if element is far enough away
      if (Math.abs(y - currentY) > 300) {
        window.scrollTo({
          top: y < 100 ? 0 : y,
          behavior: "smooth"
        });
      }
    }

    handleStandardLayout(scrollToItem) {
      if (this.config.layout === "stacked" && theme?.mediaMatches?.md) return;

      if (scrollToItem && this.state.currentItem) {
        this.viewer?.scrollTo({ left: this.state.currentItem.offsetLeft });
      }

      if (this.thumbs) {
        this.setActiveThumb();
      }
    }

    updateControls() {
      if (!this.controls) return;

      this.updateNavigationButtons();

      if (this.counterCurrent) {
        this.counterCurrent.textContent = this.state.currentIndex + 1;
      }
    }

    announceChange() {
      if (this.state.currentItem) {
        this.announceLiveRegion(this.state.currentItem, this.state.currentIndex + 1);
      }
    }

    updateXRButton() {
      if (this.xrButton && this.state.currentItem?.dataset.mediaType === "model") {
        this.xrButton.dataset.shopifyModel3dId = this.state.currentItem.dataset.mediaId;
      }
    }

    setActiveThumb() {
      if (!this.thumbs || !this.state.currentItem) return;

      this.state.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.state.currentItem.dataset.mediaId}"]`
      );

      if (!this.state.currentThumb) return;

      const btn = this.state.currentThumb.querySelector("button");
      if (!btn) return;

      // Update all thumb buttons
      this.thumbs.querySelectorAll(".media-thumbs__btn").forEach(el => {
        el.classList.remove("is-active");
        el.removeAttribute("aria-current");
      });

      // Set active thumb
      btn.classList.add("is-active");
      btn.setAttribute("aria-current", "true");
      this.checkThumbVisibilty(this.state.currentThumb);
    }

    setVisibleItems() {
      this.viewerItems = this.querySelectorAll(".media-viewer__item");
      this.visibleItems = Array.from(this.viewerItems).filter(
        el => el.clientWidth > 0
      );

      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    checkThumbVisibilty(thumb) {
      if (!this.thumbs || !thumb) return;

      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      const needsScroll =
        thumbOffset + thumb.clientWidth > lastVisibleThumbOffset ||
        thumbOffset < scrollPos;

      if (needsScroll) {
        this.thumbs.scrollTo({
          left: thumbOffset,
          behavior: "smooth"
        });
      }
    }

    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector(".media-viewer img");
      if (!image || !this.liveRegion) return;

      this.liveRegion.setAttribute("aria-hidden", "false");
      this.liveRegion.innerHTML = theme.strings?.imageAvailable?.replace(
        "[index]",
        index
      ) || `Image ${index} available`;

      setTimeout(() => {
        this.liveRegion.setAttribute("aria-hidden", "true");
      }, 2000);
    }

    static playActiveMedia(mediaItem) {
      window.pauseAllMedia?.();
      const deferredMedia = mediaItem?.querySelector("deferred-media");
      if (deferredMedia) {
        deferredMedia.loadContent?.();
      }
    }
  }

  customElements.define("media-gallery", MediaGallery);
}
